import React, { useContext, useCallback } from 'react';
import { TableContext } from '../table/context/TableContext';
import ToolbarButton from './ToolbarButton';
import FilterPopover from './popovers/FilterPopover';
import ColumnPopover from './popovers/ColumnPopover';
import DensityPopover from './popovers/DensityPopover';
import ExportPopover from './popovers/ExportPopover';
import QuickFilter from '../filters/QuickFilter';
import { useMediaQuery } from '../../../hooks/useMediaQuery';

const BaseGridToolbar = ({
  title,
  columns,
  data = [],
  actionButton,
  showQuickFilter = true,
  quickFilterProps = { debounceMs: 300 }
}) => {
  const {
    preferences: { density },
    handleDensityChange,
    sortConfig,
    handleClearSort,
    filterText,
    filters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterValueChange,
    handleFilterRemove,
    handleFilterClear,
    getDefaultOperator,
    handleLogicOperatorChange,
    columnSearchText,
    setColumnSearchText,
    isColumnVisible,
    toggleColumnVisibility,
    loading,
    filterRows
  } = useContext(TableContext);

  const [popoverState, setPopoverState] = React.useState({
    column: { open: false, anchorEl: null },
    export: { open: false, anchorEl: null },
    density: { open: false, anchorEl: null },
    filter: { open: false, anchorEl: null }
  });

  const closeAllPopovers = useCallback(() => {
    setPopoverState({
      column: { open: false, anchorEl: null },
      export: { open: false, anchorEl: null },
      density: { open: false, anchorEl: null },
      filter: { open: false, anchorEl: null }
    });
  }, []);

  const handleClick = useCallback((event, type) => {
    const isOpen = popoverState[type].open;
    if (isOpen) {
      setPopoverState(prev => ({
        ...prev,
        [type]: { open: false, anchorEl: null }
      }));
    } else {
      setPopoverState({
        column: { open: false, anchorEl: null },
        export: { open: false, anchorEl: null },
        density: { open: false, anchorEl: null },
        filter: { open: false, anchorEl: null },
        [type]: { open: true, anchorEl: event.currentTarget }
      });
    }
  }, [popoverState]);

  const handleClose = useCallback((type) => {
    setPopoverState(prev => ({
      ...prev,
      [type]: { open: false, anchorEl: null }
    }));
  }, []);

  const handleColumnClick = useCallback((event) => handleClick(event, 'column'), [handleClick]);
  const handleExportClick = useCallback((event) => handleClick(event, 'export'), [handleClick]);
  const handleDensityClick = useCallback((event) => handleClick(event, 'density'), [handleClick]);
  const handleFilterClick = useCallback((event) => handleClick(event, 'filter'), [handleClick]);

  const handleColumnClose = useCallback(() => handleClose('column'), [handleClose]);
  const handleExportClose = useCallback(() => handleClose('export'), [handleClose]);
  const handleDensityClose = useCallback(() => handleClose('density'), [handleClose]);
  const handleFilterClose = useCallback(() => handleClose('filter'), [handleClose]);

  const handleExportCSV = useCallback(() => {
    const visibleColumns = columns.filter(col => isColumnVisible(col.field));
    const headers = visibleColumns.map(col => col.headerName || col.field).join(',');
    const rows = visibleColumns.map(col => col.field);
    
    // Get filtered data from context
    const filteredData = filterRows(data).map(row => 
      rows.map(field => `"${row[field] || ''}"`).join(',')
    );

    const csv = [headers, ...filteredData].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'export.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleExportClose();
  }, [columns, isColumnVisible, filterRows, data, handleExportClose]);

  const handleExportXLSX = useCallback(() => {
    const visibleColumns = columns.filter(col => isColumnVisible(col.field));
    const headers = visibleColumns.map(col => col.headerName || col.field);
    const rows = visibleColumns.map(col => col.field);
    
    // Get filtered data from context
    const filteredData = filterRows(data).map(row => 
      rows.map(field => row[field] || '')
    );

    // Create CSV for Excel (which handles Excel's special formatting)
    const csvContent = [headers, ...filteredData].map(row => 
      row.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'application/vnd.ms-excel;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'export.xls');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleExportClose();
  }, [columns, isColumnVisible, filterRows, data, handleExportClose]);

  const hasFilters = filters.length > 0;
  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <div className="px-2 sm:px-4 py-3 bg-white border-b border-gray-200 rounded-t-xl relative z-10">
      <div className="flex flex-row items-center justify-between gap-4">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 whitespace-nowrap">
          {title}
        </h2>

        <div className="flex flex-nowrap items-center justify-end gap-2 w-full">
          {showQuickFilter && <div className="flex-grow"><QuickFilter {...quickFilterProps} /></div>}

          <div className="relative">
            <ToolbarButton
              onClick={handleFilterClick}
              isActive={hasFilters}
              startIcon={
                <div className="relative">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                  </svg>
                  {hasFilters && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-indigo-600 text-xs text-white">
                      {filters.length}
                    </span>
                  )}
                </div>
              }
            >
              {!isMobile && 'Filter'}
            </ToolbarButton>
            <FilterPopover
              id="filter-popover"
              open={popoverState.filter.open}
              anchorEl={popoverState.filter.anchorEl}
              onClose={handleFilterClose}
              columns={columns}
              filters={filters}
              onFilterAdd={handleFilterAdd}
              onFilterChange={handleFilterChange}
              onFilterValueChange={handleFilterValueChange}
              onFilterRemove={handleFilterRemove}
              onFilterClear={handleFilterClear}
              getDefaultOperator={getDefaultOperator}
              onLogicOperatorChange={handleLogicOperatorChange}
              filteredCount={filterRows(data).length}
            />
          </div>

          {!isMobile && (
            <>
              {sortConfig.length > 0 && (
                <ToolbarButton
                  onClick={handleClearSort}
                  isActive={true}
                >
                  Clear Sort
                </ToolbarButton>
              )}

              <div className="relative">
                <ToolbarButton
                  onClick={handleDensityClick}
                  startIcon={
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 6a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 6a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
                    </svg>
                  }
                >
                  <span className="hidden sm:inline">Density</span>
                </ToolbarButton>
                <DensityPopover
                  id="density-popover"
                  open={popoverState.density.open}
                  anchorEl={popoverState.density.anchorEl}
                  onClose={handleDensityClose}
                  onDensityChange={handleDensityChange}
                  currentDensity={density}
                />
              </div>

              <div className="relative">
                <ToolbarButton
                  onClick={handleExportClick}
                  startIcon={
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  }
                >
                  <span className="hidden sm:inline">Export</span>
                </ToolbarButton>
                <ExportPopover
                  id="export-popover"
                  open={popoverState.export.open}
                  anchorEl={popoverState.export.anchorEl}
                  onClose={handleExportClose}
                  onExportCSV={handleExportCSV}
                  onExportXLSX={handleExportXLSX}
                />
              </div>

              <div className="relative">
                <ToolbarButton
                  onClick={handleColumnClick}
                  startIcon={
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  }
                >
                  <span className="hidden sm:inline">Columns</span>
                </ToolbarButton>
                {!loading && (
                  <ColumnPopover
                    id="columns-popover"
                    open={popoverState.column.open}
                    anchorEl={popoverState.column.anchorEl}
                    onClose={handleColumnClose}
                    columns={columns}
                    searchText={columnSearchText}
                    onSearchChange={setColumnSearchText}
                    onColumnToggle={toggleColumnVisibility}
                    isColumnVisible={isColumnVisible}
                    onSelectAll={() => toggleColumnVisibility(columns.map(col => col.field), true)}
                  />
                )}
              </div>
            </>
          )}

          {actionButton}
        </div>
      </div>
    </div>
  );
};

export default React.memo(BaseGridToolbar);
